import * as THREE from 'three';
import { EffectComposer, EffectPass, RenderPass } from 'postprocessing';

import { Visualization } from './Visualizer.js';
import { getGlobalState } from '../Gui/store/persistent/persistentStore.js';

export class PostProcess {
    private static readonly DEFAULT_MULTISAMPLING = 32;

    renderer: THREE.WebGLRenderer;
    renderTarget: THREE.WebGLRenderTarget;
    composer: EffectComposer;
    renderPass?: RenderPass;
    effectPass?: EffectPass;
    viewer: Visualization;
    private multisampling: number;

    constructor(renderer: THREE.WebGLRenderer, viewer: Visualization) {
        this.viewer = viewer;
        this.renderer = renderer;
        this.multisampling = this.resolveMultisampling();
        this.composer = new EffectComposer(this.renderer, { multisampling: this.multisampling });
        this.renderTarget = new THREE.WebGLRenderTarget(
            window.innerWidth * window.devicePixelRatio,
            window.innerHeight * window.devicePixelRatio,
            { samples: this.multisampling }
        );
    }

    setCamera(activeCamera: THREE.Camera) {
        if (this.renderPass) this.renderPass.mainCamera = activeCamera;
        if (this.effectPass) this.effectPass.mainCamera = activeCamera;
    }

    applyPostProcess(
        pixelSize = 3,
        activeCamera: THREE.Camera = this.viewer.cameraHelper.getActiveCamera(getGlobalState().ambient.currentCamera.type)
    ) {
        const context = this.renderer.getContext();
        const contextAttributes = context?.getContextAttributes?.();
        if (!contextAttributes) {
            console.warn('PostProcess: WebGL context lost, skipping post-processing setup.');
            return;
        }

        const drawingBufferSize = this.renderer.getDrawingBufferSize(new THREE.Vector2());
        const resolutionDivider = getGlobalState().settings.lowPerformance && pixelSize !== 0 ? 3 : 1;
        const width = Math.max(1, Math.round(drawingBufferSize.width / resolutionDivider));
        const height = Math.max(1, Math.round(drawingBufferSize.height / resolutionDivider));

        this.renderTarget.setSize(width, height);
        this.syncComposerBuffers();

        if (!this.renderPass) {
            this.renderPass = new RenderPass(this.viewer.scene, activeCamera);
            this.composer.addPass(this.renderPass);
            this.effectPass = new EffectPass(activeCamera);
            this.composer.addPass(this.effectPass);
        } else {
            this.renderPass.mainScene = this.viewer.scene;
            this.renderPass.mainCamera = activeCamera;
            if (this.effectPass) {
                this.effectPass.mainCamera = activeCamera;
            }
        }

        this.renderPass?.setSize(drawingBufferSize.width, drawingBufferSize.height);
        this.effectPass?.setSize(drawingBufferSize.width, drawingBufferSize.height);

        if (getGlobalState().settings.lowPerformance || pixelSize === 0) this.lowPerformance();
        else this.highPerformance();
    }

    lowPerformance() {
        this.composer.removeAllPasses();
        this.composer.addPass(this.renderPass!);
        this.composer.addPass(this.effectPass!);
    }

    highPerformance() {
        this.composer.removeAllPasses();
        this.composer.addPass(this.renderPass!);
        this.composer.addPass(this.effectPass!);
    }

    disableAntiAliasing() {
        this.multisampling = 0;
        this.renderTarget.samples = 0;
        this.renderTarget.dispose();
        this.composer.multisampling = 0;
    }

    private resolveMultisampling() {
        const context = this.renderer.getContext();
        const maxSamples = context instanceof WebGL2RenderingContext ? context.getParameter(context.MAX_SAMPLES) : 0;
        return Math.min(PostProcess.DEFAULT_MULTISAMPLING, maxSamples || 0);
    }

    private syncComposerBuffers() {
        if (this.composer.inputBuffer !== this.renderTarget) {
            this.composer.inputBuffer.dispose();
            this.composer.outputBuffer.dispose();
            this.composer.inputBuffer = this.renderTarget;
            this.composer.outputBuffer = this.renderTarget.clone();
            return;
        }

        this.composer.outputBuffer.setSize(this.renderTarget.width, this.renderTarget.height);
    }

    dispose() {
        this.composer.dispose();
        this.renderPass?.dispose();
        this.effectPass?.dispose();
        this.renderTarget.dispose();
    }
}
